import { inject, Injectable } from "@angular/core";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, Subject, Subscription, throwError } from "rxjs";
import { catchError, filter, map, take } from "rxjs/operators";
import { CheckoutFlowService } from "src/app/services/checkout-flow.service";
import { CommonService } from "src/app/services/common/common.service";
import { MixpanelService } from "src/app/services/mixpanel.service";
import { PaymentService } from "src/app/services/payment.service";
import { VoucherService } from "src/app/services/vouchers.service";
import { StoreCheckoutFlowHost, StoreCheckoutFlowService } from "../../services/store-checkout-flow.service";
import type { CheckoutTryAgainHostContext } from "./checkout-try-again-host-context";

@Injectable({ providedIn: null })
export class CheckoutComGatewayService {
  mixPanelService = inject(MixpanelService);
  private storeCheckoutFlowService = inject(StoreCheckoutFlowService);
  private checkoutFlowService = inject(CheckoutFlowService);
  private router = inject(Router);
  private voucherService = inject(VoucherService);
  private paymentService = inject(PaymentService);
  private commonService = inject(CommonService);

  private framesInitRetryCount = 0;
  private readonly framesInitMaxRetry = 3;
  private readonly framesInitRetryDelay = 500;
  private checkoutFlowInitSubscription?: Subscription;
  private checkoutFlowInitialized = false;

  public isCheckoutCom: boolean = false;
  public isCheckoutComform: boolean = true;
  public redirectlink: any = null;
  public showCheckOutComSpinner: boolean = false;
  public isButtonDisabled: boolean = true;
  public errorMessage: string = "";

  private isCheckoutComSubject = new BehaviorSubject<boolean>(false);
  public readonly isCheckoutCom$ = this.isCheckoutComSubject.asObservable();

  private isCheckoutComformSubject = new BehaviorSubject<boolean>(true);
  public readonly isCheckoutComform$ = this.isCheckoutComformSubject.asObservable();

  private redirectlinkSubject = new BehaviorSubject<any>(null);
  public readonly redirectlink$ = this.redirectlinkSubject.asObservable();

  private showSpinnerSubject = new BehaviorSubject<boolean>(false);
  public readonly showSpinner$ = this.showSpinnerSubject.asObservable();

  private isButtonDisabledSubject = new BehaviorSubject<boolean>(true);
  public readonly isButtonDisabled$ = this.isButtonDisabledSubject.asObservable();

  private errorMessageSubject = new BehaviorSubject<string>("");
  public readonly errorMessage$ = this.errorMessageSubject.asObservable();

  public cardTokenizedSubject: Subject<any> = new Subject<any>();

  loadCheckoutApiAndInitialize(ctx: CheckoutTryAgainHostContext, queryParams: Data, actRoute: ActivatedRoute): void {
    if (this.handleCheckoutFlowRedirectReturn(ctx, queryParams, actRoute)) {
      return;
    }

    ctx.showCheckOutComSpinner = true;
    ctx.errorMessage = "Loading secure card form...";
    this.showSpinnerSubject.next(true);
    this.errorMessageSubject.next(ctx.errorMessage);
    ctx.changeDetRef.detectChanges();

    this.paymentService
      ?.getCheckoutApiDetails(queryParams)
      ?.pipe(
        catchError((err) => {
          return throwError(() => err);
        }),
      )
      .subscribe(
        (res: Data) => {
          ctx.keys = res.data;
          if (!res.data?.publicKey) {
            return;
          }

          ctx.isPaymill = false;
          ctx.isCheckoutCom = true;
          this.isCheckoutComSubject.next(true);
          ctx.changeDetRef.detectChanges();
          this.scheduleCheckoutFlowMount(ctx, actRoute);
        },
        (err) => {
          return err;
        },
      );
  }

  cancelCheckoutFlowInitSchedule(): void {
    this.checkoutFlowInitSubscription?.unsubscribe();
    this.checkoutFlowInitSubscription = undefined;
  }

  private scheduleCheckoutFlowMount(ctx: CheckoutTryAgainHostContext, actRoute: ActivatedRoute): void {
    if (this.checkoutFlowInitialized) {
      return;
    }

    const tryMount = (): boolean => {
      const purchaseDetail = ctx.purchaseDetail$?.value;
      if (!purchaseDetail || !ctx.pathString || this.checkoutFlowInitialized) {
        return;
      }

      this.checkoutFlowInitialized = true;
      this.checkoutFlowInitSubscription?.unsubscribe();
      ctx.changeDetRef.detectChanges();
      requestAnimationFrame(() => {
        void this.initializeFrames(ctx);
      });
      return true;
    };

    if (tryMount()) {
      return;
    }

    this.checkoutFlowInitSubscription?.unsubscribe();
    this.checkoutFlowInitSubscription = combineLatest([
      ctx.purchaseDetail$.pipe(filter((purchaseDetail) => !!purchaseDetail)),
      actRoute.params.pipe(
        map(() => ctx.pathString as string | undefined),
        filter((pathString) => !!pathString),
      ),
    ])
      .pipe(take(1))
      .subscribe(() => {
        tryMount();
      });
  }

  public async initializeFrames(ctx: CheckoutTryAgainHostContext): Promise<void> {
    try {
      ctx.isCheckoutComform = true;
      this.isCheckoutComformSubject.next(true);
      ctx.framesInitRetryCount = 0;

      const host = this.toFlowHost(ctx);
      host.onFlowPaymentCompleted = () => this.handleFlowPaymentConfirmation(ctx);
      host.onFlowReady = () => {
        ctx.checkoutFlowReady = true;
        ctx.showCheckOutComSpinner = false;
        ctx.isButtonDisabled = false;
        this.showSpinnerSubject.next(false);
        this.isButtonDisabledSubject.next(false);
        if (this.storeCheckoutFlowService.isCheckoutLoadingMessage(ctx.errorMessage)) {
          ctx.errorMessage = "";
          this.errorMessageSubject.next("");
        }
        ctx.changeDetRef.detectChanges();
      };
      await this.storeCheckoutFlowService.mountStoreCheckoutFlow(host);
    } catch (error) {
      if ((error as Data)?.code === "checkout_flow_mount_cancelled") { return; }
      if ((error as Data)?.code !== "checkout_flow_ready_timeout" && ctx.framesInitRetryCount < ctx.framesInitMaxRetry) {
        ctx.framesInitRetryCount++;
        setTimeout(() => {
          if(ctx.paymentMode === "sandbox") return;
          void this.initializeFrames(ctx);
        }, ctx.framesInitRetryDelay);
      } else {
        ctx.errorMessage = "Failed to initialize payment form. Please try again later.";
        ctx.showCheckOutComSpinner = false;
        this.showSpinnerSubject.next(false);
        this.errorMessageSubject.next(ctx.errorMessage);
        ctx.authService.errorHandlerLogs("Failed to initialize checkout flow", error);
      }
    }
  }

  public onCardValidationChanged(ctx: CheckoutTryAgainHostContext): void {
    ctx.isButtonDisabled = !ctx.checkoutFlowReady;
    this.isButtonDisabledSubject.next(ctx.isButtonDisabled);
  }

  public onValidationChanged(ctx: CheckoutTryAgainHostContext, _event: any): void {
    ctx.isButtonDisabled = !ctx.checkoutFlowReady;
    this.isButtonDisabledSubject.next(ctx.isButtonDisabled);
  }

  public getErrorMessage(element: string): string {
    const errors: { [key: string]: string } = {
      "card-number": "Please enter a valid card number",
      "expiry-date": "Please enter a valid expiry date",
      cvv: "Please enter a valid CVV code",
    };

    return errors[element] || "An error occurred.";
  }

  public onCardTokenizationFailed(_ctx: CheckoutTryAgainHostContext): void {
    // Flow handles tokenization internally; no-op for compatibility.
  }

  public handleFlowPaymentConfirmation(ctx: CheckoutTryAgainHostContext): void {
    ctx.addNewCardInProgress = true;
    ctx.showCheckOutComSpinner = true;
    ctx.isCheckoutComform = false;
    this.isCheckoutComformSubject.next(false);
    this.showSpinnerSubject.next(true);
    ctx.changeDetRef.detectChanges();
    ctx.loadingService.present();

    this.commonService
      .getUid("handleFlowPaymentConfirmation")
      .pipe(take(1))
      .subscribe((uid: string) => {
        if (uid) {
          ctx.paymentService.checkoutFlowUserId$.next(uid);
        }

        ctx.getCheckOutComResponseSubscription = this.storeCheckoutFlowService.waitForPaymentConfirmation(
          this.toFlowHost(ctx),
          {
            onSuccess: (paymentResData) => this.handlePaymentSuccess(ctx, paymentResData),
            onFailure: () => this.checkoutPaymentFailedFlow(ctx),
            onTimeout: () => {
              ctx.addNewCardInProgress = false;
              ctx.showCheckOutComSpinner = false;
              this.showSpinnerSubject.next(false);
              ctx.errorMessage = "Payment confirmation timed out. Please try again.";
              this.errorMessageSubject.next(ctx.errorMessage);
              ctx.loadingService.dismiss();
              ctx.changeDetRef.detectChanges();
            },
          },
        );
      });
  }

  public onSubmit(ctx: CheckoutTryAgainHostContext, event: any): void {
    event.preventDefault();
    this.storeCheckoutFlowService.submitPayment();
  }

  public checkoutPaymentFailedFlow(ctx: CheckoutTryAgainHostContext): void {
    ctx.loadingService.dismiss();
    ctx.resetPaymentSubscriptions();
    ctx.getCheckOutComResponseSubscription?.unsubscribe();
    ctx.generateCheckoutTokenSubscription?.unsubscribe();
    ctx.cardTokenizedSubject.complete();
    ctx.paymentFailRedirectionStatus = true;
    ctx.voucherService.updateDiscountVoucherHideStatus(ctx.selectedDiscountVoucherData?.id, false);
    ctx.addCardPaymentStatus = false;
    ctx.cardTokenizedCalled = false;
    ctx.isCheckoutCom = false;
    this.isCheckoutComSubject.next(false);
    ctx.timerOn = false;
    ctx.addNewCardInProgress = false;
    ctx.proceed = true;
    ctx.isPaymentDone = false;
    ctx.isCheckoutComform = true;
    this.isCheckoutComformSubject.next(true);
    ctx.redirectlink = null;
    this.redirectlinkSubject.next(null);
    ctx.timerOn = false;
    ctx.authService.getConstantsMsg("1093").then((translatedMsg) => {
      const defaultMsg = {
        message: {
          line_1: "There is an issue in processing payment",
          line_2: "",
        },
        style: {
          icon: "tick circle error.svg",
          type: "Error",
        },
      };
      ctx.authService.showToastMessage(translatedMsg, defaultMsg);
    });

    ctx.redirectToPreviousPage();
  }

  public checkAndUpdateStylesForIframe(ctx: CheckoutTryAgainHostContext): void {
    try {
      const computedStyle = window.getComputedStyle(ctx.checkOutIframe?.nativeElement);
      if (computedStyle?.getPropertyValue("left") === "-10000px") {
        ctx.checkOutIframe.nativeElement.style["left"] = "unset !important";
      }
    } catch (error) {
      return error;
    }
  }

  unmount(): void {
    this.cancelCheckoutFlowInitSchedule();
    this.storeCheckoutFlowService.unmount();
  }

  isCheckoutLoadingMessage(message: string): boolean {
    return this.storeCheckoutFlowService.isCheckoutLoadingMessage(message);
  }

  handleCheckoutFlowRedirectReturn(
    ctx: CheckoutTryAgainHostContext,
    queryParams: Data,
    actRoute: ActivatedRoute,
  ): boolean {
    const redirectReturn = this.checkoutFlowService.parseRedirectReturn(queryParams);
    if (!redirectReturn.isRedirectReturn) {
      return false;
    }

    if (redirectReturn.logId) {
      ctx.pathString = redirectReturn.logId;
      ctx.paymentService.syncCheckoutFlowStatus({ logId: redirectReturn.logId });
    }

    if (redirectReturn.userId) {
      ctx.paymentService.checkoutFlowUserId$.next(redirectReturn.userId);
    }

    ctx.isCheckoutCom = true;
    this.isCheckoutComSubject.next(true);
    ctx.addNewCardInProgress = true;
    ctx.showCheckOutComSpinner = true;
    ctx.cardTokenizedCalled = true;
    this.showSpinnerSubject.next(true);
    ctx.changeDetRef.detectChanges();

    if (redirectReturn.status === "success") {
      this.handleFlowPaymentConfirmation(ctx);
      this.clearCheckoutFlowReturnQueryParams(actRoute, queryParams);
      return true;
    }

    if (redirectReturn.status === "fail") {
      ctx.errorMessage = "Payment failed, please try again.";
      this.errorMessageSubject.next(ctx.errorMessage);
      ctx.showCheckOutComSpinner = false;
      ctx.addNewCardInProgress = false;
      this.showSpinnerSubject.next(false);
      ctx.changeDetRef.detectChanges();
      this.clearCheckoutFlowReturnQueryParams(actRoute, queryParams);
      return true;
    }

    this.clearCheckoutFlowReturnQueryParams(actRoute, queryParams);
    return true;
  }

  private clearCheckoutFlowReturnQueryParams(actRoute: ActivatedRoute, queryParams: Data): void {
    const cleaned = this.checkoutFlowService.stripRedirectReturnParams({
      ...(queryParams ?? {}),
    });
    void this.router.navigate([], {
      relativeTo: actRoute,
      queryParams: cleaned,
      replaceUrl: true,
    });
  }

  private handlePaymentSuccess(ctx: CheckoutTryAgainHostContext, paymentResData: any): void {
    const paymentResponse = paymentResData?.payment_response;
    ctx.customerPurchaseEncryptedId =
      paymentResData?.customer_purchase_encrypted_id ??
      paymentResData?.payment_request?.customer_purchase?.encrypted_customer_purchase_id;
    ctx.showCheckOutComSpinner = false;
    ctx.addNewCardInProgress = false;
    this.showSpinnerSubject.next(false);

    if (!paymentResponse?.payment_status) {
      ctx.loadingService.dismiss();
      ctx.changeDetRef.detectChanges();
      return;
    }

    if (ctx.addCardPaymentStatus) {
      ctx.loadingService.dismiss();
      ctx.changeDetRef.detectChanges();
      return;
    }

    ctx.cardTokenizedCalled = false;
    ctx.paymentHold = false;
    ctx.getCheckOutComResponseSubscription?.unsubscribe();
    ctx.generateCheckoutTokenSubscription?.unsubscribe();

    if (paymentResponse.payment_status !== "success") {
      this.checkoutPaymentFailedFlow(ctx);
      return;
    }

    ctx.paymentService.emitSyndicatePurchaseComplete();
    ctx.loadingService.dismiss();
    ctx.stepperIndex = 1;
    ctx.startCountdown();
    if (ctx.stepper?.selected) {
      ctx.stepper.selected.completed = true;
      ctx.stepper.selected.editable = false;
      ctx.stepper.next();
    }
    ctx.timerOn = false;
    ctx.voucherService.updateDiscountVoucherRedeemedStatus(ctx.selectedDiscountVoucherData, true);
    if (ctx.selectedDiscountValue > 0 && ctx.selectedDiscountVoucherData) {
      const event = ctx.selectedDiscountVoucherData.autoAppliedVoucher
        ? "voucher_auto_applied"
        : "voucher_manual_applied";
      this.mixPanelService.trackVoucherEvent(event, ctx.selectedDiscountVoucherData);
    } else {
      this.mixPanelService.trackVoucherEvent("voucher_skipped", ctx.discountVouchersData[0]);
    }
    ctx.addCardPaymentStatus = true;

    if (!paymentResponse.show_toast) {
      ctx.authService
        .getConstantsMsg("1092")
        .then((translatedMsg) => {
          const defaultMsg = {
            message: {
              line_1: "Payment has been successfully processed",
              line_2: "",
            },
            style: {
              icon: "tick circle success.png",
              type: "Success",
            },
          };
          ctx.authService.showToastMessage(translatedMsg, defaultMsg);
        })
        .catch((error) => {
          ctx.authService.errorHandlerLogs("Error fetching constants message:", error);
        });

      paymentResponse.show_toast = true;

      const path_id = `JL4LGZDCJv0wpAfFrAPc`;
      const doc_data = { payment_response: paymentResponse };
      const dv = { docId: ctx.pathString };
      const method = "update";

      ctx.authService
        .getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        })
        .subscribe({
          error: (error) => {
            return error;
          },
        });
    }

    const encryptSyndicateId =
      paymentResponse?.encrypt_syndicate_id ??
      paymentResData?.encrypt_syndicate_id ??
      paymentResData?.api_response?.response?.encrypt_syndicate_id;

    if (encryptSyndicateId) {
      ctx.paymentService.cashPurchaseAddCardSyndicateId$.next(String(encryptSyndicateId));
    }

    const purchaseDetail = ctx.purchaseDetail$?.value;
    const isTopUp = purchaseDetail?.isTopUp || purchaseDetail?.page === "topUp";
    ctx.changeDetRef.detectChanges();

    if (!isTopUp) {
      this.voucherService.setSyndicatePending().pipe(take(1)).subscribe();
      this.router.navigate(["mainTabs/tabs/syndicate"], {
        state: { encrypt_syndicate_id: encryptSyndicateId },
      });
      ctx.changeDetRef.detectChanges();
    }
  }

  private toFlowHost(ctx: CheckoutTryAgainHostContext): StoreCheckoutFlowHost {
    return {
      keys: ctx.keys,
      language: ctx.language,
      paymentMode: ctx.paymentMode,
      pathString: ctx.pathString,
      checkOutComData: ctx.checkOutComData,
      purchaseDetail$: ctx.purchaseDetail$,
      userData: ctx.userData,
      preferredCurrency: ctx.preferredCurrency,
      selectedDiscountValue: ctx.selectedDiscountValue,
      updatedPrizeValue: ctx.updatedPrizeValue,
      isCheckoutComform: ctx.isCheckoutComform,
      isButtonDisabled: ctx.isButtonDisabled,
      showCheckOutComSpinner: ctx.showCheckOutComSpinner,
      errorMessage: ctx.errorMessage,
      checkoutFlowReady: ctx.checkoutFlowReady,
      cardTokenizedCalled: ctx.cardTokenizedCalled,
      addNewCardInProgress: ctx.addNewCardInProgress,
      getCookie: (name) => ctx.getCookie(name),
      getCheckOutComResponseSubscription: ctx.getCheckOutComResponseSubscription,
    };
  }
}
